<script setup>
import { useNavbar } from '~/composables/useNavbar.js'
import { useBrand } from '~/composables/useBrand.js'

const { brandWordmark } = useBrand()

const {
  authenticated,
  impersonationTopClass,
  navRef,
  mobileOpen,
  toggleMobile,
  closeMobile,
  onLoginClick,
  startUpload,
  modalComponent,
  modalProps,
} = useNavbar()
</script>

<template>
  <nav ref="navRef" :class="impersonationTopClass" class="sticky z-[200] bg-canvas/[.92] backdrop-blur-[16px] border-b border-line px-8 max-md:px-4 h-[60px] max-md:h-[56px] flex items-center justify-between gap-6">
    <NuxtLink to="/" class="flex items-center gap-2.5 max-[443px]:gap-2 font-black text-lg text-ink cursor-pointer max-[443px]:leading-4" @click="closeMobile">
      <div class="w-[34px] h-[34px] rounded-xl flex items-center justify-center bg-grad-brand">
        <Icon name="hugeicons:ai-eraser" size="20" style="color: rgb(var(--c-on-brand))" />
      </div>
      {{ brandWordmark }}
    </NuxtLink>

    <div class="flex items-center gap-7 max-md:hidden">
      <NuxtLink to="/#how"        class="text-sm font-medium text-ink-soft cursor-pointer transition-colors duration-150 hover:text-brand-text">{{ $t('nav.how') }}</NuxtLink>
      <NuxtLink to="/examples"        class="text-sm font-medium text-ink-soft cursor-pointer transition-colors duration-150 hover:text-brand-text">{{ $t('nav.samples') }}</NuxtLink>
      <NuxtLink to="/success-stories" class="text-sm font-medium text-ink-soft cursor-pointer transition-colors duration-150 hover:text-brand-text">{{ $t('nav.cases') }}</NuxtLink>
      <NuxtLink to="/contact"         class="text-sm font-medium text-ink-soft cursor-pointer transition-colors duration-150 hover:text-brand-text">{{ $t('nav.contact') }}</NuxtLink>
    </div>

    <div class="flex items-center gap-3 max-md:gap-2">
      <button
        v-if="!authenticated"
        class="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-ink-soft rounded-xl cursor-pointer transition-colors duration-150 hover:bg-surface-sunken max-md:hidden max-[443px]:leading-4"
        @click="onLoginClick"
      >{{ $t('nav.login') }}</button>
      <NuxtLink
        v-if="authenticated"
        to="/dashboard"
        class="inline-flex items-center justify-center px-[1.35rem] py-2 max-md:px-4 max-md:py-1.5 text-sm font-bold text-on-brand bg-brand rounded-xl cursor-pointer transition-all duration-150 shadow-[0_2px_8px_rgb(var(--c-brand)/.3)] hover:bg-brand-strong hover:-translate-y-px max-[443px]:leading-4"
      >{{ $t('nav.panel') }}</NuxtLink>
      <button
        v-if="!authenticated"
        class="inline-flex items-center justify-center px-[1.35rem] py-2 max-md:px-4 max-md:py-1.5 text-sm font-bold text-on-brand bg-brand rounded-xl cursor-pointer transition-all duration-150 shadow-[0_2px_8px_rgb(var(--c-brand)/.3)] hover:bg-brand-strong hover:-translate-y-px max-[443px]:leading-4 max-md:hidden"
        @click="startUpload"
      >{{ $t('nav.start') }}</button>

      <button
        type="button"
        class="md:hidden w-10 h-10 -mr-1.5 flex items-center justify-center rounded-lg text-ink cursor-pointer transition-colors duration-150 hover:bg-surface-sunken"
        :aria-expanded="mobileOpen"
        aria-controls="mobile-menu"
        :aria-label="mobileOpen ? $t('nav.close') : $t('nav.menu')"
        @click="toggleMobile"
      >
        <Icon :name="mobileOpen ? 'hugeicons:cancel-01' : 'hugeicons:menu-01'" size="22" />
      </button>
    </div>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div
        v-if="mobileOpen"
        id="mobile-menu"
        class="md:hidden absolute top-full left-0 right-0 bg-canvas/[.98] backdrop-blur-[16px] border-b border-line shadow-shl px-4 pt-3 pb-5 flex flex-col"
      >
        <NuxtLink to="/#how"            class="py-3 px-2 text-base font-semibold text-ink border-b border-line cursor-pointer transition-colors duration-150 hover:text-brand-text" @click="closeMobile">{{ $t('nav.how') }}</NuxtLink>
        <NuxtLink to="/examples"        class="py-3 px-2 text-base font-semibold text-ink border-b border-line cursor-pointer transition-colors duration-150 hover:text-brand-text" @click="closeMobile">{{ $t('nav.samples') }}</NuxtLink>
        <NuxtLink to="/success-stories" class="py-3 px-2 text-base font-semibold text-ink border-b border-line cursor-pointer transition-colors duration-150 hover:text-brand-text" @click="closeMobile">{{ $t('nav.cases') }}</NuxtLink>
        <NuxtLink to="/contact"         class="py-3 px-2 text-base font-semibold text-ink cursor-pointer transition-colors duration-150 hover:text-brand-text" @click="closeMobile">{{ $t('nav.contact') }}</NuxtLink>
        <button
          v-if="!authenticated"
          class="inline-flex items-center justify-center mt-4 w-full py-3 text-base font-bold text-on-brand bg-brand rounded-xl cursor-pointer transition-all duration-150 shadow-[0_2px_8px_rgb(var(--c-brand)/.3)] hover:bg-brand-strong hover:-translate-y-px"
          @click="onLoginClick"
        >{{ $t('nav.login') }}</button>
      </div>
    </Transition>

  </nav>

  <ClientOnly>
    <Teleport to="body">
      <component :is="modalComponent" v-if="modalComponent" v-bind="modalProps" />
    </Teleport>
  </ClientOnly>
</template>
